import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash2, Filter, Globe, MapPin, Users, Pencil, Eye } from "lucide-react";
import { toast } from "sonner";
import CompanyProfileDialog from "@/components/CompanyProfileDialog";

const EMPTY = { name: "", industry: "", website: "", size: "", location: "", notes: "" };

export default function Companies() {
  const [companies, setCompanies] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [profileCompany, setProfileCompany] = useState(null); // opens CompanyProfileDialog

  const load = useCallback(async () => {
    const { data } = await api.get("/companies", { params: q ? { q } : {} });
    setCompanies(data);
  }, [q]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const openCreate = () => { setEditingId(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (c) => {
    setEditingId(c.id);
    setForm({
      name: c.name || "", industry: c.industry || "", website: c.website || "",
      size: c.size || "", location: c.location || "", notes: c.notes || "",
    });
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editingId) {
        await api.patch(`/companies/${editingId}`, form);
        toast.success("Company updated");
      } else {
        await api.post("/companies", form);
        toast.success("Company created");
      }
      setOpen(false); setForm(EMPTY); setEditingId(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this company?")) return;
    try {
      await api.delete(`/companies/${id}`);
      toast.success("Company removed");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-4 md:p-8 space-y-6" data-testid="companies-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow mb-2">Accounts</div>
          <h1 className="text-3xl tracking-tight font-medium">Companies</h1>
          <p className="text-sm text-muted-foreground mt-1">{companies.length} total</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
            <Input data-testid="companies-search" placeholder="Filter companies — name or industry…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 w-72" />
          </div>
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditingId(null); setForm(EMPTY); } }}>
            <DialogTrigger asChild>
              <Button data-testid="new-company-btn" onClick={openCreate} className="bg-primary hover:bg-primary/90 text-primary-foreground">
                <Plus className="h-4 w-4 mr-1.5" /> New company
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingId ? "Edit company" : "New company"}</DialogTitle>
                <DialogDescription className="sr-only">
                  Enter company details. Name is required.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label>Name</Label>
                  <Input data-testid="company-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Industry</Label>
                    <Input data-testid="company-industry" value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Website</Label>
                    <Input data-testid="company-website" placeholder="example.com" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Size</Label>
                    <Input data-testid="company-size" placeholder="1-10" value={form.size} onChange={(e) => setForm({ ...form, size: e.target.value })} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Location</Label>
                    <Input data-testid="company-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)} data-testid="company-cancel">Cancel</Button>
                <Button onClick={save} disabled={saving || !form.name} data-testid="company-save" className="bg-primary text-primary-foreground hover:bg-primary/90">
                  {saving ? "Saving…" : editingId ? "Save" : "Create"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {companies.length === 0 && (<div className="text-sm text-muted-foreground col-span-full">No companies yet.</div>)}
        {companies.map((c) => (
          <div key={c.id} data-testid={`company-card-${c.id}`}
               className="card-flat p-5 flex flex-col gap-3 hover:-translate-y-0.5 hover:shadow-md transition-[transform,box-shadow] duration-200">
            <div className="flex items-start justify-between">
              <button
                type="button"
                onClick={() => setProfileCompany(c)}
                className="min-w-0 text-left group"
                data-testid={`open-company-${c.id}`}
                aria-label={`Open ${c.name} profile`}
              >
                <div className="text-xs text-muted-foreground uppercase tracking-[0.2em]">{c.industry || "Company"}</div>
                <div className="text-lg font-medium tracking-tight mt-1 group-hover:text-primary transition-colors duration-150" style={{ fontFamily: "Outfit" }}>{c.name}</div>
              </button>
              <div className="flex flex-col gap-1">
                <button
                  data-testid={`view-company-${c.id}`}
                  onClick={() => setProfileCompany(c)}
                  className="text-muted-foreground hover:text-primary transition-colors duration-150"
                  aria-label="View company profile"
                >
                  <Eye className="h-4 w-4" />
                </button>
                <button
                  data-testid={`edit-company-${c.id}`}
                  onClick={() => openEdit(c)}
                  className="text-muted-foreground hover:text-primary transition-colors duration-150"
                  aria-label="Edit company"
                >
                  <Pencil className="h-4 w-4" />
                </button>
                <button
                  data-testid={`delete-company-${c.id}`}
                  onClick={() => remove(c.id)}
                  className="text-muted-foreground hover:text-destructive transition-colors duration-150"
                  aria-label="Delete company"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div className="text-xs text-muted-foreground space-y-1 mt-auto">
              {c.website && (<div className="flex items-center gap-1.5"><Globe className="h-3.5 w-3.5" strokeWidth={1.75} />{c.website}</div>)}
              {c.location && (<div className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" strokeWidth={1.75} />{c.location}</div>)}
              {c.size && (<div className="flex items-center gap-1.5"><Users className="h-3.5 w-3.5" strokeWidth={1.75} />{c.size} employees</div>)}
            </div>
          </div>
        ))}
      </div>

      <CompanyProfileDialog
        company={profileCompany}
        open={!!profileCompany}
        onOpenChange={(o) => { if (!o) setProfileCompany(null); }}
      />
    </div>
  );
}
